import {
  Card,
  Dropdown,
  Icon,
  IconName,
  Loader,
  MultipleChoice,
} from '@unmind/design-system';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import { AppointmentMode } from '__generated__/globalTypes';
import i18n from 'i18n/config';
import { Footer } from '../Footer';
import { BookingError } from '../BookingError';
import { useBookingFilterOptions } from './../../hooks';
import { changeStep } from './../../lib';
import { Fieldset } from './Preferences.styles';
import { IPreferencesProps } from './types';

export default ({
  dispatch,
  errors,
  answers,
  service,
  step,
}: IPreferencesProps) => {
  const { data, loading, error } = useBookingFilterOptions({
    serviceId: service.id,
  });

  const { search } = useLocation();
  const { t } = useTranslation('book', {
    keyPrefix: 'service.form',
  });

  const searchParams = new URLSearchParams(search);
  const expertise = searchParams.get('expertise') || undefined;
  const serviceHasExpertises =
    data?.bookingFilterOptions?.expertises &&
    data?.bookingFilterOptions?.expertises.length > 0;
  const fallbackTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  useEffect(() => {
    if (serviceHasExpertises && expertise) {
      const expertiseInService = data?.bookingFilterOptions?.expertises?.find(
        ({ id }) => id.toString() === expertise,
      );
      if (expertiseInService) {
        dispatch({ type: 'answers.1.expertises', value: [expertise] });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expertise, serviceHasExpertises]);

  useEffect(() => {
    if (
      data?.bookingFilterOptions?.languages &&
      i18n.language &&
      !answers.languages?.length
    ) {
      const countryCode = i18n.language.split('-')[0];
      const language = data.bookingFilterOptions.languages.find(
        l => l.code === countryCode,
      );

      if (language) {
        dispatch({
          type: 'answers.1.languages',
          value: [language.id.toString()],
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.bookingFilterOptions?.languages, i18n.language]);

  useEffect(() => {
    if ((!answers.timeZone || answers.timeZone === '') && fallbackTimeZone) {
      dispatch({
        type: 'answers.1.timeZone',
        value: fallbackTimeZone,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fallbackTimeZone]);

  if (loading) {
    return (
      <Fieldset>
        <Loader variant="text" text={t('loading')} />
      </Fieldset>
    );
  }
  if (error) {
    return (
      <Fieldset>
        <BookingError />
      </Fieldset>
    );
  }

  const validateAnswers = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (
      serviceHasExpertises &&
      (!answers.expertises || answers.expertises.length === 0)
    ) {
      dispatch({
        type: 'errors.expertises',
        value: t('preferences.expertise.error'),
      });

      return;
    }

    if (!answers.languages || answers.languages.length === 0) {
      dispatch({
        type: 'errors.language',
        value: t('preferences.language.error'),
      });

      return;
    }

    if (!answers.timeZone) {
      dispatch({
        type: 'errors.timeZone',
        value: t('preferences.timezone.error'),
      });

      return;
    }

    changeStep({ step, dispatch });
  };

  const modesList = [
    { key: 'phone', value: AppointmentMode.PHONE, icon: IconName.Phone },
    { key: 'video', value: AppointmentMode.VIDEO, icon: IconName.VideoFull },
  ] as const;
  const modes: {
    label: React.ReactNode;
    value: (typeof modesList)[number]['value'];
  }[] = [];

  modesList.forEach(({ key, value, icon }) => {
    modes.push({
      label: (
        <>
          <Icon iconName={icon} /> {t(`preferences.mode.${key}.label`)}{' '}
          <small>{t(`preferences.mode.${key}.small`)}</small>
        </>
      ),
      value,
    });
  });

  return (
    <>
      <Fieldset data-testid="form">
        {serviceHasExpertises ? (
          <Dropdown
            id="expertise"
            canSelectMultiple={true}
            options={
              data?.bookingFilterOptions.expertises.map(({ id, name }) => ({
                label: name,
                value: id.toString(),
              })) ?? []
            }
            selectedValue={answers?.expertises}
            onChange={values => {
              dispatch([
                {
                  type: 'answers.1.expertises',
                  value: values,
                },
                { type: 'errors.expertises', value: undefined },
              ]);
            }}
            placeholder={t('preferences.expertise.placeholder')}
            additionalText={{
              label: t('preferences.expertise.label'),
              helpText: t('preferences.expertise.small'),
            }}
            error={errors?.expertises}
          />
        ) : (
          <></>
        )}

        <Dropdown
          id="language"
          options={
            data?.bookingFilterOptions.languages.map(({ id, name }) => ({
              label: name,
              value: id.toString(),
            })) ?? []
          }
          selectedValue={answers?.languages?.[0]}
          onChange={value => {
            dispatch([
              {
                type: 'answers.1.languages',
                value: [value],
              },
              { type: 'errors.language', value: undefined },
            ]);
          }}
          placeholder={t('preferences.language.placeholder')}
          additionalText={{
            label: t('preferences.language.label'),
            helpText: t('preferences.language.small'),
          }}
          error={errors?.language}
        />

        <Dropdown
          id="timeZone"
          options={
            data?.bookingFilterOptions.timeZones.map(({ code, name }) => ({
              label: name,
              value: code,
            })) ?? []
          }
          selectedValue={answers.timeZone}
          onChange={value => {
            dispatch([
              {
                type: 'answers.1.timeZone',
                value: value,
              },
              { type: 'errors.timeZone', value: undefined },
            ]);
          }}
          placeholder={t('preferences.timezone.placeholder')}
          additionalText={{
            label: t('preferences.timezone.label'),
          }}
          error={errors?.timeZone}
        />

        <MultipleChoice
          className="mode"
          type="radio"
          legend={t('preferences.mode.label')}
          options={modes}
          onSelected={selectedOption => {
            dispatch({
              type: 'answers.1.appointmentMode',
              value: selectedOption,
            });
          }}
          values={[answers.appointmentMode]}
        />

        {answers.appointmentMode === AppointmentMode.VIDEO && (
          <Card
            tone="nested"
            styling="no-border"
            className="notes"
            data-testid="notes"
          >
            <p>{t('preferences.mode.video.notes')}</p>
          </Card>
        )}
      </Fieldset>

      <Footer
        step={step}
        dispatch={dispatch}
        forwardCTA={{
          validate: validateAnswers,
        }}
      />
    </>
  );
};
